'use client';

import React from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faBookmark, faClose } from '@fortawesome/pro-solid-svg-icons';
import { faGlobe } from '@fortawesome/pro-regular-svg-icons';
import { useCodeSetDropdown } from './hooks';
import {
  DropdownHeader,
  ColumnHeaders,
  SectionAccordion,
} from './components';

interface AnalyticsSelectorDropdownProps {
  allowMultiple?: boolean;
  location?: string;
  noteText?: React.ReactNode;
  onCreateNew?: () => void;
}

const AnalyticsSelectorDropdown: React.FC<AnalyticsSelectorDropdownProps> = ({
  allowMultiple = false,
  location,
  noteText,
  onCreateNew,
}) => {
  const {
    selectedCodeSet,
    isOpen,
    isLoading,
    hasValue,
    label,
    isReferringDisabled,
    dropdownPosition,
    organizedData,
    expandedFolder,
    expandedSections,
    dropdownRef,
    buttonRef,
    toggleDropdown,
    handleItemSelection,
    handleClearSelection,
    handleModeToggle,
    getCodeSetInstanceCount,
    getActiveModesForCodeSet,
    toggleFolder,
    toggleSection,
  } = useCodeSetDropdown(location);

  const icon = hasValue ? faBookmark : faGlobe;

  return (
    <div
      className="relative w-64 self-stretch inline-flex flex-col justify-start items-start gap-1"
      data-testid="analytics-selector-dropdown"
    >
      {/* Dropdown Button */}
      <div
        ref={buttonRef}
        className="self-stretch px-4 h-9 bg-white rounded-full border border-neutral-200 inline-flex justify-between gap-2 items-center cursor-pointer
        hover:outline-neutral-400"
        onClick={toggleDropdown}
      >
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <Icon icon={icon} className="text-neutral-600 w-4 h-4" />
          </div>
          <div
            className={`self-stretch justify-start text-sm font-medium font-['Inter'] leading-5 line-clamp-1 whitespace-nowrap overflow-hidden text-ellipsis ${
              hasValue ? 'text-primary' : 'text-neutral-500'
            }`}
          >
            {isLoading ? 'Loading...' : label}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <div
            className={`items-center transition-all duration-300 ${
              hasValue ? 'flex' : 'hidden'
            }`}
            onClick={handleClearSelection}
          >
            <Icon
              icon={faClose}
              className="w-4 h-4 text-neutral-600 hover:text-neutral-800"
            />
          </div>
          <Icon
            icon={faCaretDown}
            className={`text-neutral-600 w-4 h-4 transition-all duration-300 ${
              isOpen ? '-rotate-90' : 'rotate-0'
            }`}
            aria-hidden="true"
          />
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen &&
        !isLoading &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed inline-flex flex-col justify-start items-start
          w-96 bg-white rounded-xl shadow-sm border border-neutral-200
          z-50 pb-2"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
            data-testid="analytics-selector-dropdown-menu"
          >
            <DropdownHeader noteText={noteText} allowMultiple={allowMultiple} />
            <div
              className={`self-stretch w-full overflow-x-hidden ${allowMultiple ? 'grid grid-cols-[1fr_auto_auto]' : 'flex flex-col'}`}
            >
              <ColumnHeaders allowMultiple={allowMultiple} onCreateNew={onCreateNew ? () => { toggleDropdown(); onCreateNew(); } : undefined} />
              <div
                className={`self-stretch overflow-hidden w-full ${allowMultiple ? 'col-span-full grid grid-cols-subgrid' : 'flex flex-col'}`}
              >
                {organizedData.sections.map((section) => {
                  const lowercaseKey = section.toLowerCase();
                  const sectionData = organizedData.data[lowercaseKey];

                  return (
                    <SectionAccordion
                      key={section}
                      section={section}
                      sectionData={sectionData}
                      isExpanded={expandedSections === lowercaseKey}
                      onToggleSection={toggleSection}
                      expandedFolder={expandedFolder}
                      toggleFolder={toggleFolder}
                      getCodeSetInstanceCount={getCodeSetInstanceCount}
                      handleItemSelection={handleItemSelection}
                      allowMultiple={allowMultiple}
                      isLimitReached={
                        allowMultiple && selectedCodeSet.length >= 10
                      }
                      handleModeToggle={handleModeToggle}
                      getActiveModesForCodeSet={getActiveModesForCodeSet}
                      isReferringDisabled={isReferringDisabled}
                    />
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default AnalyticsSelectorDropdown;
